import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Plane, MapPin, Globe, TrainFront, Clock, Car, Hotel, Landmark,
  Lightbulb, HelpCircle, ShieldCheck, TowerControl, TrendingUp, ExternalLink,
  Headphones, Square, DoorOpen,
} from "lucide-react";
import api from "../lib/api";
import IAReviews from "../components/india/IAReviews";
import IAPromoStrip from "../components/india/IAPromoStrip";

/* Indian Airports Platform — airport profile portal (Phase 1).
   Every airport is a mini portal: essentials nav, verified facts with
   source + last-verified, flights, transport, hotels, nearby, tips, FAQ. */

export default function IndiaAirportDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [speaking, setSpeaking] = useState(false);

  const listenProfile = () => {
    const synth = window.speechSynthesis;
    if (!synth || !a) return;
    if (speaking || synth.speaking) { synth.cancel(); setSpeaking(false); return; }
    const e2 = a.enrichment || {};
    const text = [
      `${a.name}, ${a.iata}, in ${a.city}, ${a.state}.`,
      e2.introduction || "",
      e2.distanceToCity ? `Distance to the city: ${e2.distanceToCity}.` : "",
      ...(e2.travelTips || []).slice(0, 3).map((t) => `Tip: ${t}`),
    ].join(" ").slice(0, 3500);
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    try {
      const p = JSON.parse(localStorage.getItem("tp_tts") || "{}");
      if (p.voiceURI) {
        const v = synth.getVoices().find((x) => x.voiceURI === p.voiceURI);
        if (v) u.voice = v;
      }
      if (p.rate) u.rate = p.rate;
    } catch { /* default voice */ }
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(u);
    // Safety: if the engine never actually starts (no voices in this browser), reset the label
    setTimeout(() => { if (!synth.speaking) setSpeaking(false); }, 1500);
  };

  useEffect(() => {
    setA(null); setErr(false);
    api.get(`/india/airports/${slug}`).then((r) => {
      setA(r.data);
      document.title = r.data.enrichment?.seoTitle || `${r.data.name} (${r.data.iata}) — Indian Airports`;
    }).catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (err) return <div className="max-w-4xl mx-auto px-5 py-16 text-center text-sm text-zinc-500" data-testid="ia-detail-notfound">Airport not found. <Link to="/india/airports/directory" className="text-blue-700 font-700 hover:underline">Browse all Indian airports</Link></div>;
  if (!a) return <div className="max-w-4xl mx-auto px-5 py-16 text-sm text-zinc-500">Loading airport…</div>;

  const e = a.enrichment || {};
  // Section nav derives from the sections that actually exist — no dead anchors
  const sections = [
    ["overview", "Overview", true],
    ["flights", "Flights", !!e.flightInfo],
    ["terminals", "Terminals", a.terminal_guide?.terminals?.length > 0],
    ["facilities", "Facilities", e.facilities?.length > 0],
    ["transport", "Getting There", e.groundTransport?.length > 0],
    ["hotels", "Hotels", e.nearbyHotels?.length > 0],
    ["nearby", "Nearby", e.tourismNearby?.length > 0],
    ["tips", "Tips", e.travelTips?.length > 0 || !!e.expansion],
    ["faq", "FAQ", e.faq?.length > 0],
    ["reviews", "Reviews", true],
  ].filter(([, , show]) => show);
  const facts = [
    ["Operator", a.operator], ["Opened", a.opened], ["Elevation", a.elevation_m != null ? `${a.elevation_m} m` : null],
    ["Runways", a.runways], ["Also known as", a.alt_name], ["Region", a.region],
    ["Distance to city", e.distanceToCity], ["Metro connected", a.has_metro ? "Yes" : null],
  ].filter(([, v]) => v != null && v !== "");

  return (
    <div data-testid="india-airport-detail">
      {/* Branded masthead strip — emerald shade for airport profiles */}
      <div className="bg-gradient-to-r from-emerald-50 via-white to-teal-50 border-b border-zinc-200" data-testid="ia-detail-masthead">
        <div className="max-w-5xl mx-auto px-5 py-2.5 flex items-center gap-2.5 min-w-0">
          <Link to="/india/airports" className="flex items-center gap-2 min-w-0 shrink" data-testid="ia-detail-masthead-brand">
            <span className="inline-flex p-1.5 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow shrink-0"><TowerControl className="w-4 h-4" /></span>
            <span className="font-heading font-900 text-base leading-none whitespace-nowrap"><span className="text-blue-800">Indian</span><span className="text-orange-500">Airports</span><span className="text-zinc-400 text-xs">.com</span></span>
          </Link>
          <span className="hidden sm:inline text-[10px] font-800 uppercase tracking-[0.18em] text-emerald-800">· Every Airport. Every Journey.</span>
          <span className="ml-auto font-mono font-800 text-xs bg-white border border-emerald-200 text-emerald-800 rounded px-2 py-0.5 shrink-0">{a.iata}</span>
        </div>
      </div>

      {/* Hero */}
      <div className="relative h-56 sm:h-72">
        <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-zinc-900/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-5xl mx-auto px-5 pb-5 text-white">
          <Link to="/india/airports/directory" className="text-xs font-700 inline-flex items-center gap-1 opacity-90 hover:opacity-100" data-testid="ia-detail-back"><ArrowLeft className="w-3.5 h-3.5" /> All Indian airports</Link>
          <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight mt-1" data-testid="ia-detail-name">{a.name}</h1>
          <div className="mt-1 text-sm flex flex-wrap items-center gap-x-3 gap-y-1 opacity-95">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {a.city}, {a.state}</span>
            <span className="font-mono font-800 bg-white/20 rounded px-1.5">{a.iata}</span>
            <span className="font-mono opacity-80">{a.icao}</span>
            {a.tagline && <span className="opacity-90">· {a.tagline}</span>}
          </div>
        </div>
      </div>

      {/* Essentials nav */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-zinc-200 overflow-x-auto">
        <div className="max-w-5xl mx-auto px-5 flex gap-1 py-2" data-testid="ia-detail-nav">
          {sections.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="shrink-0 text-xs font-700 text-zinc-600 hover:text-blue-700 hover:bg-blue-50 rounded-full px-3 py-1.5 transition-colors">{label}</a>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8 space-y-10">
        {/* Overview */}
        <section id="overview" data-testid="ia-sec-overview">
          {e.introduction && <p className="text-[15px] leading-relaxed text-zinc-700">{e.introduction}</p>}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {facts.map(([k, v]) => (
              <div key={k} className="bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5">
                <div className="text-[10px] font-800 uppercase tracking-wide text-zinc-400">{k}</div>
                <div className="text-sm font-700 text-zinc-800 mt-0.5">{v}</div>
              </div>
            ))}
          </div>
          {e.stats && Object.keys(e.stats).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="ia-detail-stats">
              {Object.entries(e.stats).map(([k, v]) => v && (
                <span key={k} className="inline-flex items-center gap-1.5 bg-sky-50 border border-sky-200 text-sky-900 rounded-full px-3 py-1.5 text-xs font-600">
                  <TrendingUp className="w-3 h-3" /> {k.replace(/([A-Z])/g, " $1").toLowerCase()}: <b>{v}</b>
                </span>
              ))}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {a.website && (
              <a href={a.website} target="_blank" rel="noreferrer" data-testid="ia-detail-website"
                className="inline-flex items-center gap-1.5 text-xs font-700 border border-zinc-300 rounded-full px-3.5 py-2 hover:border-blue-500"><Globe className="w-3.5 h-3.5" /> Official website <ExternalLink className="w-3 h-3 text-zinc-400" /></a>
            )}
            <Link to="/plan-ai" data-testid="ia-detail-plan-trip"
              className="inline-flex items-center gap-1.5 bg-blue-700 text-white text-xs font-700 rounded-full px-4 py-2 hover:bg-blue-800"><Plane className="w-3.5 h-3.5" /> Plan a trip to {a.city} on TravelerPulse</Link>
            <button onClick={listenProfile} data-testid="ia-detail-listen"
              className={`inline-flex items-center gap-1.5 text-xs font-700 rounded-full px-4 py-2 border transition-colors ${speaking ? "bg-rose-50 border-rose-300 text-rose-700" : "border-zinc-300 text-zinc-700 hover:border-blue-500"}`}>
              {speaking ? <Square className="w-3.5 h-3.5" /> : <Headphones className="w-3.5 h-3.5" />} {speaking ? "Stop" : "Listen to this guide"}
            </button>
          </div>
        </section>

        {/* Flights */}
        {e.flightInfo && (
          <section id="flights" data-testid="ia-sec-flights">
            <H2 icon={Plane}>Flights & airlines</H2>
            <div className="mt-3 grid sm:grid-cols-2 gap-4">
              {e.flightInfo.domestic?.length > 0 && <ChipCard title="Domestic destinations" tone="blue"
                items={e.flightInfo.domestic.map((n) => ({ label: n, to: a.route_targets?.[n] ? `/india/airports/routes/${a.route_targets[n]}` : null }))} />}
              {e.flightInfo.international?.length > 0 && <ChipCard title="International destinations" tone="violet"
                items={e.flightInfo.international.map((n) => ({ label: n, to: a.route_targets?.[n] ? `/india/airports/routes/${a.route_targets[n]}` : null }))} />}
            </div>
            {e.flightInfo.airlines?.length > 0 && <div className="mt-3"><ChipCard title="Airlines serving this airport" tone="emerald"
              items={e.flightInfo.airlines.map((n) => ({ label: n, to: `/india/airlines/${n.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}` }))} /></div>}
            {e.flightInfo.cargo && <p className="mt-3 text-sm text-zinc-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">Cargo: {e.flightInfo.cargo}</p>}
          </section>
        )}

        {/* Terminal guides (major metro airports — AI-assisted editorial) */}
        {a.terminal_guide?.terminals?.length > 0 && (
          <section id="terminals" data-testid="ia-sec-terminals">
            <H2 icon={DoorOpen}>Terminal guide</H2>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {a.terminal_guide.terminals.map((t) => (
                <div key={t.name} className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid={`ia-terminal-${t.name.replace(/\s+/g, "-")}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-800">{t.name}</span>
                    {t.focus && <span className="text-[10px] font-700 uppercase bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">{t.focus}</span>}
                  </div>
                  <p className="mt-1.5 text-sm text-zinc-600">{t.guide}</p>
                  {t.tips?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {t.tips.map((tip, i) => <li key={i} className="text-xs text-zinc-500 flex items-start gap-1.5"><span className="text-amber-500">•</span> {tip}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            {a.terminal_guide.transfer_note && <p className="mt-3 text-sm text-zinc-600 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3"><b>Transfers:</b> {a.terminal_guide.transfer_note}</p>}
            <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block" data-testid="ia-terminal-disclaimer">{a.terminal_guide.label}</p>
          </section>
        )}

        {/* Facilities */}
        {e.facilities?.length > 0 && (
          <section id="facilities" data-testid="ia-sec-facilities">
            <H2 icon={TowerControl}>Terminal facilities</H2>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {e.facilities.map((f) => (
                <div key={f.name} className="bg-white border border-zinc-200 rounded-xl px-4 py-3">
                  <div className="text-sm font-700">{f.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{f.desc}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Transport */}
        {e.groundTransport?.length > 0 && (
          <section id="transport" data-testid="ia-sec-transport">
            <H2 icon={Car}>Getting to the airport</H2>
            <div className="mt-3 space-y-2">
              {e.groundTransport.map((g) => (
                <div key={g.mode} className="flex flex-wrap items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-3">
                  <span className="font-700 text-sm w-24 shrink-0">{g.mode}</span>
                  <span className="text-sm text-zinc-600 flex-1 min-w-[200px]">{g.detail}</span>
                  {g.fare && !/^(n\/a|varies)$/i.test(g.fare.trim()) && <span className="text-xs font-700 text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">{g.fare}</span>}
                  {g.time && <span className="text-xs text-zinc-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {g.time}</span>}
                </div>
              ))}
            </div>
            {a.has_metro && <p className="mt-2 text-xs text-emerald-700 font-600 inline-flex items-center gap-1"><TrainFront className="w-3.5 h-3.5" /> This airport has a metro connection.</p>}
          </section>
        )}

        {/* Hotels */}
        {e.nearbyHotels?.length > 0 && (
          <section id="hotels" data-testid="ia-sec-hotels">
            <H2 icon={Hotel}>Hotels near {a.iata}</H2>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {e.nearbyHotels.map((h) => (
                <div key={h.name} className="bg-white border border-zinc-200 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-700">{h.name}</span>
                    {h.category && <span className="text-[10px] font-800 uppercase bg-zinc-100 text-zinc-500 rounded px-1.5 py-0.5">{h.category}</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {h.distanceKm != null && `${h.distanceKm} km from airport`}{h.priceRange ? ` · ${h.priceRange}` : ""}
                  </div>
                  {h.notes && <p className="text-xs text-zinc-600 mt-1">{h.notes}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Nearby */}
        {e.tourismNearby?.length > 0 && (
          <section id="nearby" data-testid="ia-sec-nearby">
            <H2 icon={Landmark}>Beyond the airport</H2>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {e.tourismNearby.map((tp) => (
                <div key={tp.name} className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-xl px-4 py-3">
                  <div className="text-sm font-700">{tp.name} <span className="text-[10px] font-600 text-orange-700">{tp.type}</span></div>
                  <div className="text-xs text-zinc-600 mt-0.5">{tp.description}</div>
                  <div className="text-[11px] text-zinc-400 mt-1">{tp.distanceKm != null && `${tp.distanceKm} km away`}{tp.bestSeason ? ` · best: ${tp.bestSeason}` : ""}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Tips + expansion */}
        {(e.travelTips?.length > 0 || e.expansion) && (
          <section id="tips" data-testid="ia-sec-tips">
            <H2 icon={Lightbulb}>Travel tips</H2>
            {e.travelTips?.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {e.travelTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-xl px-4 py-2.5">
                    <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /> {tip}
                  </li>
                ))}
              </ul>
            )}
            {e.expansion && <p className="mt-3 text-sm text-zinc-600 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3"><b>Development:</b> {e.expansion}</p>}
          </section>
        )}

        {/* FAQ */}
        {e.faq?.length > 0 && (
          <section id="faq" data-testid="ia-sec-faq">
            <H2 icon={HelpCircle}>Frequently asked questions</H2>
            <div className="mt-3 space-y-1.5">
              {e.faq.map((f, i) => (
                <div key={i} className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`ia-faq-${i}`}
                    className="w-full text-left px-4 py-3 text-sm font-600 flex items-center gap-2 hover:bg-zinc-50">
                    <span className="flex-1">{f.q}</span>
                    <span className="text-zinc-400">{openFaq === i ? "−" : "+"}</span>
                  </button>
                  {openFaq === i && <p className="px-4 pb-3 text-sm text-zinc-600">{f.a}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Structured traveler reviews */}
        <section id="reviews" data-testid="ia-sec-reviews">
          <IAReviews airport={a} />
        </section>

        {/* TravelerPulse cross-promo */}
        <IAPromoStrip city={a.city} />

        {/* Provenance (content governance §38-39) */}
        <p className="text-xs text-zinc-400 border-t border-zinc-100 pt-4 flex items-start gap-1.5" data-testid="ia-detail-provenance">
          <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-500" />
          <span>Last verified {a.last_verified}. Airport information changes — always confirm critical details with the airport or your airline.
            {a.source?.name && <> Source: {a.source.name}.</>} Found something outdated? <a href={`mailto:support@travelerpulse.pro?subject=Correction: ${a.iata}`} className="text-blue-700 hover:underline">Report incorrect information</a>.</span>
        </p>
      </div>
    </div>
  );
}

function H2({ icon: Icon, children }) {
  return <h2 className="font-heading font-800 text-lg md:text-lg flex items-center gap-2"><Icon className="w-5 h-5 text-blue-700" /> {children}</h2>;
}

function ChipCard({ title, items, tone }) {
  const tones = {
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    violet: "bg-violet-50 border-violet-200 text-violet-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-xl px-4 py-3">
      <div className="text-xs font-800 uppercase tracking-wide text-zinc-400">{title}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.map((x) => {
          const label = typeof x === "string" ? x : x.label;
          const to = typeof x === "string" ? null : x.to;
          const cls = `text-xs font-600 border rounded-full px-2.5 py-1 ${tones[tone]}`;
          return to
            ? <Link key={label} to={to} className={`${cls} underline decoration-dotted underline-offset-2 hover:opacity-80`}>{label}</Link>
            : <span key={label} className={cls}>{label}</span>;
        })}
      </div>
    </div>
  );
}
